import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {NzMessageService} from "ng-zorro-antd/message";
import {NzModalService} from "ng-zorro-antd/modal";
import {lastValueFrom} from "rxjs";
import {
    PerFormaAPIService,
    ZonalMetaDataDefinitionDto,
    ZonalPageDto,
    ZonalRegionDto
} from "src/app/services/pfapi.service";
import {ZonalBox} from "src/directives/zonal-region.directive";

// region draw types — mirror the legacy regionDrawingType values.
const REGION_TYPE_DOCTYPE = 0;
const REGION_TYPE_METADATA = 1;
const REGION_TYPE_DOCTYPE_IMAGE = 2;

// the image is rendered/measured against a fixed 1020x1320 pixel space (backend contract).
const IMAGE_WIDTH = 1020;
const IMAGE_HEIGHT = 1320;

// region model used in the template — pixel coords in 1020x1320 space plus a precomputed css class.
interface RegionModel {
    regionID: string;
    box: ZonalBox;
    regionType: number;
    metaDataDefinitionID: number | null;
    documentTypeID: number | null;
    cssClass: string;
}

@Component({
    selector: "app-zonal-template-edit",
    templateUrl: "./zonal-template-edit.component.html",
    styleUrl: "./zonal-template-edit.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ZonalTemplateEditComponent implements OnInit, OnDestroy {
    readonly imageWidth = IMAGE_WIDTH;
    readonly imageHeight = IMAGE_HEIGHT;

    readonly regionTypeDoctype = REGION_TYPE_DOCTYPE;
    readonly regionTypeMetadata = REGION_TYPE_METADATA;
    readonly regionTypeDoctypeImage = REGION_TYPE_DOCTYPE_IMAGE;

    templateId: number | null = null;
    pages: ZonalPageDto[] = [];
    selectedPageId: number | null = null;
    regions: RegionModel[] = [];

    // precomputed button classes per page id (no function calls in template).
    pageButtonClass: Record<number, string> = {};

    regionDrawingType = REGION_TYPE_METADATA;
    loading = true;
    imageUrl: SafeUrl | null = null;

    // drawing a new region with a click+drag on the canvas.
    drawing = false;
    drawStart: {x: number; y: number} | null = null;
    drawBox: ZonalBox | null = null;

    // add-page modal.
    addPageVisible = false;
    addPageForm: FormGroup;
    addPageFile: File | null = null;

    // edit-page modal.
    editPageVisible = false;
    editPageForm: FormGroup;

    // edit-metadata-region modal.
    editRegionVisible = false;
    editRegionForm: FormGroup;
    editingRegionId: string | null = null;
    metaDataDefinitions: ZonalMetaDataDefinitionDto[] = [];

    private objectUrl: string | null = null;

    constructor(
        private readonly api: PerFormaAPIService,
        private readonly route: ActivatedRoute,
        private readonly fb: FormBuilder,
        private readonly message: NzMessageService,
        private readonly modal: NzModalService,
        private readonly sanitizer: DomSanitizer
    ) {
        this.addPageForm = this.fb.group({
            pageNumber: [null]
        });
        this.editPageForm = this.fb.group({
            pageNumber: [null, [Validators.required]]
        });
        this.editRegionForm = this.fb.group({
            metaDataDefinitionID: [null]
        });
    }

    async ngOnInit(): Promise<void> {
        const idParam = this.route.snapshot.queryParamMap.get("templateId");
        this.templateId = idParam ? Number(idParam) : null;

        if (this.templateId == null || isNaN(this.templateId)) {
            this.message.error("No template specified");
            this.loading = false;
            return;
        }

        await this.loadPages(true);
    }

    ngOnDestroy(): void {
        this.revokeObjectUrl();
    }

    private async loadPages(selectFirst: boolean): Promise<void> {
        this.loading = true;
        try {
            const result = await lastValueFrom(this.api.getApiConfigureZonalTemplatesTemplateIdPages(this.templateId));
            this.pages = result.Pages ?? [];
            this.recomputePageButtons();

            if (this.pages.length > 0) {
                const target = selectFirst ? this.pages[0] : this.pages[this.pages.length - 1];
                await this.changePage(target.ZonalTemplatePageID);
            } else {
                this.selectedPageId = null;
                this.regions = [];
                this.revokeObjectUrl();
            }
        } catch (error) {
            this.message.error("Failed to load template pages");
            console.log(error);
        } finally {
            this.loading = false;
        }
    }

    private recomputePageButtons(): void {
        const map: Record<number, string> = {};
        for (const page of this.pages) {
            map[page.ZonalTemplatePageID] =
                page.ZonalTemplatePageID === this.selectedPageId ? "btn btn-primary" : "btn btn-default";
        }
        this.pageButtonClass = map;
    }

    async changePage(pageId: number): Promise<void> {
        if (pageId == null || pageId === this.selectedPageId) {
            this.recomputePageButtons();
            return;
        }
        this.loading = true;
        this.selectedPageId = pageId;
        this.recomputePageButtons();
        this.loadPageImage(pageId);

        try {
            const page = await lastValueFrom(this.api.getApiConfigureZonalPagesPageId(pageId));
            this.regions = (page.Regions ?? []).map(r => this.toRegionModel(r));
        } catch (error) {
            this.message.error("Failed to load page regions");
            console.log(error);
        } finally {
            this.loading = false;
        }
    }

    private loadPageImage(pageId: number): void {
        this.revokeObjectUrl();
        this.api.getApiConfigureZonalPagesPageIdImage(pageId).subscribe({
            next: blob => {
                this.objectUrl = URL.createObjectURL(blob);
                this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(this.objectUrl);
            },
            error: err => {
                console.log(err);
                this.imageUrl = null;
            }
        });
    }

    private revokeObjectUrl(): void {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
        this.imageUrl = null;
    }

    private toRegionModel(dto: ZonalRegionDto): RegionModel {
        return {
            regionID: dto.RegionID,
            box: {
                x: dto.RegionX ?? 0,
                y: dto.RegionY ?? 0,
                width: dto.RegionWidth ?? 0,
                height: dto.RegionHeight ?? 0
            },
            regionType: dto.RegionType ?? REGION_TYPE_METADATA,
            metaDataDefinitionID: dto.MetaDataDefinitionID ?? null,
            documentTypeID: dto.DocumentTypeID ?? null,
            cssClass: this.regionClass(dto.RegionType ?? REGION_TYPE_METADATA)
        };
    }

    private regionClass(regionType: number): string {
        switch (regionType) {
            case REGION_TYPE_DOCTYPE:
                return "canvas-box canvas-box-yellow";
            case REGION_TYPE_METADATA:
                return "canvas-box canvas-box-green";
            case REGION_TYPE_DOCTYPE_IMAGE:
                return "canvas-box canvas-box-blue";
            default:
                return "canvas-box";
        }
    }

    changeRegionDrawType(newType: number): void {
        this.regionDrawingType = newType;
    }

    onImageLoad(): void {
        this.loading = false;
    }

    // sync a region's geometry from the drag/resize directive output.
    onRegionBoxChange(region: RegionModel, box: ZonalBox): void {
        region.box = box;
    }

    removeRegion(regionId: string): void {
        this.regions = this.regions.filter(r => r.regionID !== regionId);
    }

    // ---- canvas draw (create a new region) ----

    onCanvasPointerDown(event: PointerEvent): void {
        // only start a draw when clicking the bare canvas, not an existing region/button.
        const target = event.target as HTMLElement;
        if (target.closest(".canvas-box")) {
            return;
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        this.drawStart = {
            x: this.clampX(event.clientX - rect.left),
            y: this.clampY(event.clientY - rect.top)
        };
        this.drawBox = {x: this.drawStart.x, y: this.drawStart.y, width: 0, height: 0};
        this.drawing = true;
    }

    onCanvasPointerMove(event: PointerEvent): void {
        if (!this.drawing || !this.drawStart) {
            return;
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const curX = this.clampX(event.clientX - rect.left);
        const curY = this.clampY(event.clientY - rect.top);
        this.drawBox = {
            x: Math.min(this.drawStart.x, curX),
            y: Math.min(this.drawStart.y, curY),
            width: Math.abs(curX - this.drawStart.x),
            height: Math.abs(curY - this.drawStart.y)
        };
    }

    onCanvasPointerUp(): void {
        if (!this.drawing) {
            return;
        }
        this.drawing = false;
        const box = this.drawBox;
        this.drawBox = null;
        this.drawStart = null;

        // ignore stray clicks (no meaningful drag).
        if (!box || box.width < 5 || box.height < 5) {
            return;
        }

        const region: RegionModel = {
            regionID: crypto.randomUUID(),
            box,
            regionType: this.regionDrawingType,
            metaDataDefinitionID: null,
            documentTypeID: null,
            cssClass: this.regionClass(this.regionDrawingType)
        };
        this.regions = [...this.regions, region];
    }

    private clampX(value: number): number {
        return Math.max(0, Math.min(IMAGE_WIDTH, value));
    }

    private clampY(value: number): number {
        return Math.max(0, Math.min(IMAGE_HEIGHT, value));
    }

    // ---- save ----

    async saveRegions(): Promise<void> {
        if (this.selectedPageId == null) {
            return;
        }
        this.loading = true;

        const regions: ZonalRegionDto[] = this.regions.map(r => ({
            RegionID: r.regionID,
            RegionX: Math.round(r.box.x),
            RegionY: Math.round(r.box.y),
            RegionWidth: Math.round(r.box.width),
            RegionHeight: Math.round(r.box.height),
            RegionType: r.regionType as ZonalRegionDto["RegionType"],
            MetaDataDefinitionID: r.metaDataDefinitionID,
            DocumentTypeID: r.documentTypeID
        }));

        try {
            await lastValueFrom(this.api.postApiConfigureZonalRegions({PageID: this.selectedPageId, Regions: regions}));
            this.message.success("Regions saved");
            // refresh page info so server-assigned values are reflected.
            const page = await lastValueFrom(this.api.getApiConfigureZonalPagesPageId(this.selectedPageId));
            this.regions = (page.Regions ?? []).map(r => this.toRegionModel(r));
        } catch (error) {
            this.message.error("Failed to save regions");
            console.log(error);
        } finally {
            this.loading = false;
        }
    }

    // ---- add page ----

    openAddPage(): void {
        this.addPageForm.reset();
        this.addPageFile = null;
        this.addPageVisible = true;
    }

    onAddPageFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.addPageFile = input.files && input.files.length ? input.files[0] : null;
    }

    async addPage(): Promise<void> {
        if (!this.addPageFile) {
            this.message.error("Select an image or PDF");
            return;
        }
        this.addPageVisible = false;
        this.loading = true;

        const pageNumber = this.addPageForm.value.pageNumber;
        try {
            const result = await lastValueFrom(
                this.api.postApiConfigureZonalPages({
                    templateId: this.templateId,
                    pageNumber: pageNumber != null ? String(pageNumber) : undefined,
                    file: this.addPageFile
                })
            );
            this.pages = result.Pages ?? [];
            this.recomputePageButtons();
            if (this.pages.length > 0) {
                await this.changePage(this.pages[this.pages.length - 1].ZonalTemplatePageID);
            }
        } catch (error) {
            this.message.error("Failed to add page");
            console.log(error);
        } finally {
            this.loading = false;
        }
    }

    // ---- edit page ----

    openEditPage(): void {
        const current = this.pages.find(p => p.ZonalTemplatePageID === this.selectedPageId);
        this.editPageForm.setValue({pageNumber: current ? current.ZonalPageNumber : null});
        this.editPageVisible = true;
    }

    async editPage(): Promise<void> {
        if (this.editPageForm.invalid || this.selectedPageId == null) {
            this.editPageForm.markAllAsTouched();
            return;
        }
        this.editPageVisible = false;
        this.loading = true;
        try {
            const result = await lastValueFrom(
                this.api.putApiConfigureZonalPages({
                    PageID: this.selectedPageId,
                    PageNumber: Number(this.editPageForm.value.pageNumber)
                })
            );
            this.pages = result.Pages ?? [];
            this.recomputePageButtons();
        } catch (error) {
            this.message.error("Failed to edit page");
            console.log(error);
        } finally {
            this.loading = false;
        }
    }

    // ---- delete page ----

    deletePage(): void {
        if (this.selectedPageId == null) {
            return;
        }
        this.modal.confirm({
            nzTitle: "Are you sure?",
            nzContent: "This will delete the current page.",
            nzOkDanger: true,
            nzOnOk: () => this.doDeletePage()
        });
    }

    private async doDeletePage(): Promise<void> {
        this.loading = true;
        try {
            const result = await lastValueFrom(this.api.deleteApiConfigureZonalPagesPageId(this.selectedPageId));
            this.pages = result.Pages ?? [];
            this.selectedPageId = null;
            this.recomputePageButtons();
            if (this.pages.length > 0) {
                await this.changePage(this.pages[0].ZonalTemplatePageID);
            } else {
                this.regions = [];
                this.revokeObjectUrl();
            }
        } catch (error) {
            this.message.error("Failed to delete page");
            console.log(error);
        } finally {
            this.loading = false;
        }
    }

    // ---- edit metadata region ----

    async editRegion(region: RegionModel): Promise<void> {
        // only metadata regions are editable (legacy behavior).
        if (region.regionType === REGION_TYPE_DOCTYPE || region.regionType === REGION_TYPE_DOCTYPE_IMAGE) {
            return;
        }
        this.editingRegionId = region.regionID;

        if (this.metaDataDefinitions.length === 0 && this.selectedPageId != null) {
            try {
                const result = await lastValueFrom(
                    this.api.getApiConfigureZonalPagesPageIdMetadata(this.selectedPageId)
                );
                this.metaDataDefinitions = result.MetaDataDefinitions ?? [];
            } catch (error) {
                this.message.error("Failed to load metadata definitions");
                console.log(error);
            }
        }

        this.editRegionForm.setValue({metaDataDefinitionID: region.metaDataDefinitionID ?? null});
        this.editRegionVisible = true;
    }

    saveEditRegion(): void {
        const region = this.regions.find(r => r.regionID === this.editingRegionId);
        if (region) {
            const value = this.editRegionForm.value.metaDataDefinitionID;
            region.metaDataDefinitionID = value != null ? value : null;
        }
        this.editRegionVisible = false;
        this.editingRegionId = null;
    }
}
